import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  AccessTime as ClockIcon,
  Today as TodayIcon,
  CheckCircle as ApprovedIcon,
  HourglassEmpty as PendingIcon,
  Close as CloseIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import api from '../services/api';

const EmployeeTimesheets = () => {
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form data for new timesheet
  const [formData, setFormData] = useState({
    work_date: new Date().toISOString().split('T')[0],
    start_time: '08:00',
    end_time: '17:00',
    break_minutes: '60',
    work_type: 'regular',
    location: '',
    description: ''
  });

  // Summary stats
  const [stats, setStats] = useState({
    totalHoursThisWeek: 0,
    totalHoursThisMonth: 0,
    pendingApproval: 0,
    overtimeHours: 0
  });

  useEffect(() => {
    const loggedIn = localStorage.getItem('employeeLoggedIn');
    if (!loggedIn) {
      navigate('/employee/login');
      return;
    }
    fetchTimesheets();
  }, [navigate]);

  const fetchTimesheets = async () => {
    try {
      const response = await api.get('/employee/timesheets');
      setTimesheets(response.data.timesheets || []);
      setStats(response.data.stats || {
        totalHoursThisWeek: 0,
        totalHoursThisMonth: 0,
        pendingApproval: 0,
        overtimeHours: 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      setTimesheets([]);
      setLoading(false);
    }
  };

  const calculateHours = () => {
    const start = new Date(`2000-01-01T${formData.start_time}`);
    const end = new Date(`2000-01-01T${formData.end_time}`);
    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);
    const breakHours = parseInt(formData.break_minutes) / 60;
    const totalHours = Math.max(0, diffHours - breakHours);
    const overtimeHours = Math.max(0, totalHours - 8);
    
    return {
      total_hours: totalHours.toFixed(2),
      overtime_hours: overtimeHours.toFixed(2)
    };
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    
    try {
      const hours = calculateHours();
      const submitData = {
        ...formData,
        ...hours
      };
      
      await api.post('/employee/timesheets', submitData);
      setSuccess('Timesheet submitted successfully!');
      setOpenDialog(false);
      setFormData({
        work_date: new Date().toISOString().split('T')[0],
        start_time: '08:00',
        end_time: '17:00',
        break_minutes: '60',
        work_type: 'regular',
        location: '',
        description: ''
      });
      fetchTimesheets();
    } catch (error) {
      setError('Failed to submit timesheet. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getStatusChip = (status) => {
    switch(status) {
      case 'approved':
        return <Chip icon={<ApprovedIcon />} label="Approved" color="success" size="small" />;
      case 'pending':
        return <Chip icon={<PendingIcon />} label="Pending" color="warning" size="small" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" />;
      default:
        return <Chip label="Draft" size="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="fixed" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => navigate('/employee/dashboard')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            My Timesheets
          </Typography>
          <Button 
            color="inherit" 
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Add Timesheet
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: 10, pb: 3 }}>
        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ClockIcon sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    This Week
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {stats.totalHoursThisWeek}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DateRangeIcon sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    This Month
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {stats.totalHoursThisMonth}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PendingIcon sx={{ color: '#ed6c02', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Pending
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                  {stats.pendingApproval}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ClockIcon sx={{ color: '#9c27b0', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Overtime
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                  {stats.overtimeHours}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Timesheets Table */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Recent Timesheets
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {timesheets.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell>Break</TableCell>
                    <TableCell>Total Hours</TableCell>
                    <TableCell>Overtime</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timesheets.map((timesheet) => (
                    <TableRow key={timesheet.id}>
                      <TableCell>
                        {new Date(timesheet.work_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{timesheet.start_time}</TableCell>
                      <TableCell>{timesheet.end_time}</TableCell>
                      <TableCell>{timesheet.break_minutes} min</TableCell>
                      <TableCell>
                        <strong>{timesheet.total_hours}h</strong>
                      </TableCell>
                      <TableCell>
                        {timesheet.overtime_hours > 0 && (
                          <Chip label={`+${timesheet.overtime_hours}h`} size="small" color="secondary" />
                        )}
                      </TableCell>
                      <TableCell>{timesheet.work_type}</TableCell>
                      <TableCell>{getStatusChip(timesheet.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No timesheets submitted yet
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setOpenDialog(true)}
                sx={{ mt: 2, bgcolor: '#1976d2' }}
              >
                Submit Your First Timesheet
              </Button>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Add Timesheet Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Add Timesheet</Typography>
            <IconButton onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Work Date"
                type="date"
                name="work_date"
                value={formData.work_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Break (minutes)"
                type="number"
                name="break_minutes"
                value={formData.break_minutes}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Work Type</InputLabel>
                <Select
                  name="work_type"
                  value={formData.work_type}
                  onChange={handleChange}
                  label="Work Type"
                >
                  <MenuItem value="regular">Regular</MenuItem>
                  <MenuItem value="overtime">Overtime</MenuItem>
                  <MenuItem value="holiday">Holiday</MenuItem>
                  <MenuItem value="sick">Sick Leave</MenuItem>
                  <MenuItem value="vacation">Vacation</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., Berlin, Germany"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
		label="Description (Optional)"
		name="description"
		value={formData.description}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" color="text.secondary">
                  Calculated Hours: <strong>{calculateHours().total_hours}</strong>
                </Typography>
                {parseFloat(calculateHours().overtime_hours) > 0 && (
                  <Typography variant="body2" color="secondary">
                    Overtime: <strong>{calculateHours().overtime_hours}</strong>
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#1976d2' }}>
            Submit Timesheet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeTimesheets;
